import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { User, FileText, Search, Loader2, Clock, Calendar, AlertTriangle, Lock, Plus, Scissors, ArrowLeft, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Modal from '../ui/Modal';
import PrimaryButton from '../ui/PrimaryButton';
import CustomSelect from '../ui/CustomSelect';
import DatePicker from '../ui/DatePicker';
import TimePicker from '../ui/TimePicker';
import CountryPhoneInput from '../ui/CountryPhoneInput';
import Input from '../ui/Input';

import { useUsers } from '@/contexts/UsersContext';
import { useEvents } from '@/contexts/EventsContext';
import { useClients } from '@/contexts/ClientsContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { calculateTimeConstraints, validateBookingSlot, ValidationContext } from '@/lib/bookingValidation';
import { validatePhone } from '@/lib/validator';
import { recordBookingDiscount } from '@/services/events/bookingDiscountService';

interface NewAppointmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialDate?: Date;
    initialTime?: string;
    initialStaffId?: string;
    initialPerformanceId?: string;
}

const NewAppointmentModal: React.FC<NewAppointmentModalProps> = ({
    isOpen,
    onClose,
    initialDate,
    initialTime,
    initialStaffId,
    initialPerformanceId
}) => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === 'en' ? 'en-US' : 'it-IT';
    const navigate = useNavigate();

    // Contexts
    const { users } = useUsers();
    const {
        bookings,
        performances,
        busySlots,
        shiftChanges,
        createBooking,
        getPerformancesByHairdresser,
    } = useEvents();
    const { clients, blackList, customerReports: allCustomerReports, createClient } = useClients();
    const { organization, isAdmin, canSeeAllAgenda, user } = useAuth();

    // Stati Form
    const [customerInput, setCustomerInput] = useState('');
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [staffId, setStaffId] = useState<string>('');
    const [performanceId, setPerformanceId] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
    const [time, setTime] = useState<string>('');
    const [notes, setNotes] = useState<string>('');

    const [recurrenceType, setRecurrenceType] = useState<string>('none');
    const [recurrenceCount, setRecurrenceCount] = useState<string>('none');

    const [showRecurrence, setShowRecurrence] = useState(false);

    // Sconto/Maggiorazione (come in EditAppointmentModal)
    const [adjustmentType, setAdjustmentType] = useState<'none' | 'discount' | 'markup'>('none');
    const [adjustmentValue, setAdjustmentValue] = useState<string>('');
    const [adjustmentUnit, setAdjustmentUnit] = useState<'eur' | 'percent'>('eur');
    const [showAdjustment, setShowAdjustment] = useState<boolean>(false);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [clientSuggestions, setClientSuggestions] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Nuovo cliente creato al volo, senza uscire dalla prenotazione.
    const [isNewClientInline, setIsNewClientInline] = useState(false);
    const [newClientName, setNewClientName] = useState('');
    const [newClientPhone, setNewClientPhone] = useState('');
    const [isCreatingClient, setIsCreatingClient] = useState(false);

    // Quando si arriva da uno slot, giorno/ora/operatore sono già decisi e restano
    // come riepilogo: si aprono solo se serve cambiarli.
    const [showDetails, setShowDetails] = useState(false);

    // Calcolo durata iniziale (se presente)
    const initialDuration = useMemo(() => {
        if (!initialPerformanceId || !performances) return null;
        const perf = performances.find((p: any) => p.id === initialPerformanceId);
        return perf?.duration || null;
    }, [initialPerformanceId, performances]);

    // Reset all'apertura
    useEffect(() => {
        if (isOpen) {
            setCustomerInput('');
            setSelectedClientId(null);
            setStaffId(initialStaffId || (!canSeeAllAgenda && user?.id ? user.id : ''));
            setPerformanceId(initialPerformanceId || '');
            setSelectedDate(initialDate || undefined);
            setTime(initialTime || '');
            setRecurrenceType('none');
            setRecurrenceCount('none');
            setShowRecurrence(false);
            setAdjustmentType('none');
            setAdjustmentValue('');
            setAdjustmentUnit('eur');
            setShowAdjustment(false);
            setClientSuggestions([]);
            setShowSuggestions(false);
            setIsNewClientInline(false);
            setNewClientName('');
            setNewClientPhone('');
            // Senza slot di partenza non c'è nulla da riepilogare: i campi si aprono subito.
            setShowDetails(!initialTime);
        }
    }, [isOpen, initialDate, initialTime, initialStaffId, initialPerformanceId, isAdmin, user]);

    // Logica Ricerca Clienti
    useEffect(() => {
        if (!isOpen) return;
        const q = customerInput.trim().toLowerCase();
        const isExactMatch = clients?.find((c: any) => c.id === selectedClientId && c.full_name?.toLowerCase() === q);
        if (isExactMatch) {
            setShowSuggestions(false);
            return;
        }
        if (q.length < 2) {
            setClientSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        const results = (clients || []).filter((c: any) => {
            if (c.is_active === false) return false;
            const name = (c.full_name || '').toLowerCase();
            const phone = (c.phone || '').toLowerCase();
            return name.includes(q) || phone.includes(q);
        });
        setClientSuggestions(results.slice(0, 5));
        setShowSuggestions(true);
    }, [customerInput, clients, selectedClientId, isOpen]);

    const activeStaff = useMemo(() => {
        let availableStaff = users.filter((u: any) => u.is_active !== false);

        if (initialPerformanceId && selectedDate && time && organization) {
            const validationContext: ValidationContext = {
                bookings,
                performances,
                busySlots,
                organization,
                users,
                shiftChanges
            };
            const currentPerf = performances?.find((p: any) => p.id === performanceId);
            const currentDuration = currentPerf?.duration || 0;
            const currentName = currentPerf?.name || null;

            availableStaff = availableStaff.filter((staff: any) => {
                const staffPerfs = performances?.filter((p: any) => p.hairdresser_id === staff.id && p.is_active !== false) || [];

                // 1. Cerca servizio con lo stesso nome che entra nello slot
                if (currentName) {
                    const sameServicePerf = staffPerfs.find((p: any) => p.name === currentName);
                    if (sameServicePerf) {
                        const res = validateBookingSlot(validationContext, { date: selectedDate, time, performanceId: sameServicePerf.id, staffId: staff.id });
                        if (res.isValid) return true;
                    }
                }

                // 2. Cerca servizio con la stessa durata che entra nello slot
                const matchingPerf = staffPerfs.find((p: any) => p.duration === currentDuration);
                if (matchingPerf) {
                    const res = validateBookingSlot(validationContext, { date: selectedDate, time, performanceId: matchingPerf.id, staffId: staff.id });
                    if (res.isValid) return true;
                }

                // 3. Cerca un qualsiasi altro servizio che entra nello slot
                return staffPerfs.some((p: any) => {
                    const res = validateBookingSlot(validationContext, { date: selectedDate, time, performanceId: p.id, staffId: staff.id });
                    return res.isValid;
                });
            });
        }

        return availableStaff.map((u: any) => ({
            value: u.id,
            label: `${u.first_name} ${u.last_name}`
        }));
    }, [users, initialPerformanceId, selectedDate, time, organization, bookings, performances, busySlots, performanceId]);

    const performanceOptions = useMemo(() => {
        if (!performances) return [];
        let available = performances;
        if (staffId) {
            const byStaff = getPerformancesByHairdresser(staffId);
            if (byStaff) available = byStaff;
        }

        // Se siamo in modalità "Slot" (abbiamo un initialPerformanceId), 
        // filtriamo per mostrare solo servizi che "ci stanno" fisicamente
        if (initialPerformanceId && selectedDate && time && staffId && organization) {
            const validationContext: ValidationContext = {
                bookings,
                performances,
                busySlots,
                organization,
                users,
                shiftChanges
            };

            available = available.filter((p: any) => {
                const res = validateBookingSlot(validationContext, {
                    date: selectedDate,
                    time: time,
                    performanceId: p.id,
                    staffId: staffId
                });
                return res.isValid;
            });
        }

        return available
            .filter((p: any) => p.is_active !== false)
            .map((p: any) => {
                const name = p.name || t('new_appointment.service_fallback');
                return { value: p.id, label: `${name} (${p.duration ?? 0} min)` };
            });
    }, [bookings, performances, staffId, getPerformancesByHairdresser, t, initialPerformanceId, selectedDate, time, organization, users, busySlots]);

    // Servizi come pill selezionabili, con durata e prezzo a vista.
    // Senza operatore non ha senso mostrarli: i servizi (e i prezzi) sono suoi.
    const performancePills = useMemo(() => {
        if (!staffId) return [];
        return performanceOptions.map((opt: any) => {
            const perf = performances?.find((p: any) => p.id === opt.value);
            return {
                id: opt.value,
                name: perf?.name || t('new_appointment.service_fallback'),
                duration: perf?.duration ?? 0,
                price: perf?.price ?? 0,
            };
        });
    }, [performanceOptions, performances, staffId, t]);

    const selectedClient = useMemo(
        () => (clients || []).find((c: any) => c.id === selectedClientId) || null,
        [clients, selectedClientId]
    );

    const selectedStaffName = useMemo(() => {
        const u = users.find((x: any) => x.id === staffId);
        return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '';
    }, [users, staffId]);

    const getInitials = (fullName: string) =>
        fullName
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((part: string) => part[0]?.toUpperCase() ?? '')
            .join('');

    // Apre la creazione inline portandosi dietro quello che è già stato digitato:
    // se sono cifre è un numero di telefono, altrimenti è un nome.
    const openInlineNewClient = () => {
        const typed = customerInput.trim();
        const looksLikePhone = /\d/.test(typed) && !/[a-zA-Z]/.test(typed);
        setNewClientName(looksLikePhone ? '' : typed);
        setNewClientPhone(looksLikePhone ? typed : '');
        setIsNewClientInline(true);
        setShowSuggestions(false);
    };

    const handleCreateClientInline = async () => {
        const trimmedName = newClientName.trim();
        const trimmedPhone = newClientPhone.trim();

        if (!trimmedName) {
            toast.error(t('new_appointment.new_client.name_required', 'Inserisci il nome del cliente.'));
            return;
        }
        if (!trimmedPhone) {
            toast.error(t('new_appointment.new_client.phone_required', 'Inserisci il numero di telefono.'));
            return;
        }
        const phoneCheck = validatePhone(trimmedPhone);
        if (!phoneCheck.isValid) {
            toast.error(phoneCheck.message);
            return;
        }

        // La prima parola è il nome, il resto è il cognome (logica storica del campo unico)
        const [firstName, ...rest] = trimmedName.split(' ');
        setIsCreatingClient(true);
        const res = await createClient({
            first_name: firstName,
            last_name: rest.join(' '),
            phone: trimmedPhone,
            email: null,
        });
        setIsCreatingClient(false);

        if (!res?.success || !res.data) {
            toast.error(res?.error || t('common.error'));
            return;
        }

        // Il cliente appena creato è già quello della prenotazione: niente seconda ricerca.
        setSelectedClientId(res.data.id);
        setCustomerInput(res.data.full_name || trimmedName);
        setIsNewClientInline(false);
        setNewClientName('');
        setNewClientPhone('');
    };

    // Calcolo durata del servizio
    const currentServiceDuration = useMemo(() => {
        const perf = performances?.find((p: any) => p.id === performanceId);
        return perf?.duration || 0;
    }, [performanceId, performances]);

    // Prezzo del servizio e euro di sconto/maggiorazione
    const currentServicePrice = useMemo(() => {
        const perf = performances?.find((p: any) => p.id === performanceId);
        return perf?.price || 0;
    }, [performanceId, performances]);

    const adjustmentEuro = useMemo(() => {
        const v = Number(adjustmentValue) || 0;
        if (adjustmentUnit === 'percent') return Math.round((currentServicePrice * v) / 100);
        return Math.round(v);
    }, [adjustmentValue, adjustmentUnit, currentServicePrice]);

    const isDiscountTooHigh = adjustmentType === 'discount' && adjustmentEuro > currentServicePrice;
    const isPercentTooHigh = adjustmentUnit === 'percent' && Number(adjustmentValue) > 100;

    // Calcolo vincoli orari (apertura, chiusura, pause) tramite utility centralizzata
    const timeConstraints = useMemo(() => {
        return calculateTimeConstraints(
            selectedDate as Date,
            organization,
            staffId,
            users,
            performanceId,
            performances,
            shiftChanges
        );
    }, [selectedDate, organization, staffId, users, performanceId, performances]);

    // Validazione
    const canSubmit =
        !!selectedClientId &&
        !!staffId &&
        !!performanceId &&
        !!selectedDate &&
        !!time &&
        !!organization?.id &&
        !isDiscountTooHigh &&
        !isPercentTooHigh;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        try {
            setIsSubmitting(true);

            // Determina le date per la ricorrenza
            const datesToBook: Date[] = [];
            const count = recurrenceCount !== 'none' ? parseInt(recurrenceCount, 10) : 1;

            for (let i = 0; i < count; i++) {
                const nextDate = new Date(selectedDate!);
                if (recurrenceType === 'weekly') {
                    nextDate.setDate(nextDate.getDate() + (i * 7));
                } else if (recurrenceType === 'biweekly') {
                    nextDate.setDate(nextDate.getDate() + (i * 14));
                } else if (recurrenceType === 'triweekly') {
                    nextDate.setDate(nextDate.getDate() + (i * 21));
                } else if (recurrenceType === 'monthly') {
                    nextDate.setMonth(nextDate.getMonth() + i);
                } else if (recurrenceType === 'bimonthly') {
                    nextDate.setMonth(nextDate.getMonth() + (i * 2));
                }
                datesToBook.push(nextDate);
            }

            // Sconto/Maggiorazione: euro applicati (negativo = sconto)
            let priceAdjustment = 0;
            if (adjustmentType === 'discount') priceAdjustment = -Math.abs(adjustmentEuro);
            else if (adjustmentType === 'markup') priceAdjustment = Math.abs(adjustmentEuro);

            // Eseguiamo le prenotazioni in sequenza
            for (const dateToBook of datesToBook) {
                const yyyy = dateToBook.getFullYear();
                const mm = String(dateToBook.getMonth() + 1).padStart(2, '0');
                const dd = String(dateToBook.getDate()).padStart(2, '0');
                const dateStr = `${yyyy}-${mm}-${dd}`;

                const payload = {
                    hairdresser_id: staffId,
                    performance_id: performanceId,
                    shop_id: organization?.id ?? null,
                    date: dateStr,
                    time: time.length === 5 ? `${time}:00` : time,
                    client_id: selectedClientId,
                    status: 'confirmed',
                    notes: notes || null,
                    price_adjustment: priceAdjustment,
                };

                const result = await createBooking(payload as any);
                if (!result?.success) {
                    toast.error(`${result?.error || t('common.error')}`);
                    // Potremmo decidere di fermarci o continuare, qui ci fermiamo per sicurezza
                    return;
                }

                // Audit sconto sulla prenotazione appena creata (serve l'id restituito da createBooking)
                if (adjustmentType === 'discount' && adjustmentEuro > 0 && result.data?.id && organization?.id) {
                    await recordBookingDiscount({
                        booking_id: Number(result.data.id),
                        shop_id: Number(organization.id),
                        type: adjustmentUnit === 'percent' ? 'percent' : 'fixed',
                        value: Math.round(Number(adjustmentValue)) || 0,
                        amount: adjustmentEuro,
                        applied_by: user?.id ?? null,
                    });
                }
            }

            toast.success(t('new_appointment.success_message'));
            onClose();
        } catch (err) {
            toast.error(t('common.error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                className="max-w-lg"
                compact
                headerContent={
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{t('new_appointment.title')}</h2>

                        {/* Riepilogo dello slot scelto: giorno, ora e operatore a colpo d'occhio.
                            Dice già cosa si sta prenotando, quindi il sottotitolo è superfluo. */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            {selectedDate && (
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-clip-50 border border-clip-200 text-clip-700 py-1 px-2.5 rounded-full">
                                    <Calendar size={13} />
                                    <span className="capitalize">{selectedDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                                </span>
                            )}
                            {time && (
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-clip-50 border border-clip-200 text-clip-700 py-1 px-2.5 rounded-full">
                                    <Clock size={13} />
                                    {time}
                                </span>
                            )}
                            {selectedStaffName && (
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-clip-50 border border-clip-200 text-clip-700 py-1 px-2.5 rounded-full">
                                    <Scissors size={13} />
                                    {selectedStaffName}
                                </span>
                            )}
                            {!showDetails && (
                                <button
                                    type="button"
                                    onClick={() => setShowDetails(true)}
                                    className="inline-flex items-center gap-1 text-xs font-bold text-clip-600 hover:text-clip-800 underline underline-offset-2"
                                >
                                    <Pencil size={12} />
                                    {t('new_appointment.change_details', 'Cambia data, ora o operatore')}
                                </button>
                            )}
                        </div>
                    </div>
                }
            >
                {/* pt fisso: il respiro sotto l'intestazione non deve dipendere
                    dalla presenza del tasto "Cambia" */}
                <div className="space-y-4 pt-3">
                    {/* --------- CLIENTE --------- */}
                    <div className="relative z-[70]">
                        {/* Cliente scelto. Le iniziali stanno nella card: l'avatarone
                            centrato costava un terzo di schermata e non diceva nulla in più. */}
                        {selectedClient && !isNewClientInline ? (
                            <div className="flex items-center gap-3 p-3 bg-clip-50 border border-clip-200 rounded-2xl">
                                <div className="w-9 h-9 flex-shrink-0 rounded-full bg-white border border-clip-200 flex items-center justify-center text-xs font-bold text-clip-600">
                                    {getInitials(selectedClient.full_name || '') || <User size={16} strokeWidth={2} />}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-gray-900 truncate">{selectedClient.full_name}</p>
                                    <p className="text-xs text-gray-500 truncate">
                                        {selectedClient.phone || t('new_appointment.no_phone', 'Nessun numero')}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { setSelectedClientId(null); setCustomerInput(''); }}
                                    className="flex-shrink-0 text-xs font-bold text-clip-600 hover:text-clip-800 underline underline-offset-2"
                                >
                                    {t('new_appointment.change_client', 'Cambia')}
                                </button>
                            </div>
                        ) : isNewClientInline ? (
                            /* Cliente nuovo, creato senza uscire dalla prenotazione */
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-clip-600">
                                        {t('new_appointment.new_client.title', 'Nuovo cliente')}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => { setIsNewClientInline(false); setNewClientName(''); setNewClientPhone(''); }}
                                        className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700"
                                    >
                                        <ArrowLeft size={13} /> {t('new_appointment.new_client.back', 'Torna alla ricerca')}
                                    </button>
                                </div>
                                <Input
                                    type="text"
                                    autoFocus
                                    placeholder={t('new_appointment.new_client.name_placeholder', 'Nome completo')}
                                    value={newClientName}
                                    onChange={(e) => setNewClientName(e.target.value)}
                                />
                                <CountryPhoneInput
                                    value={newClientPhone}
                                    onChange={setNewClientPhone}
                                    placeholder={t('new_appointment.new_client.phone_placeholder', 'Numero di telefono')}
                                />
                                <button
                                    type="button"
                                    onClick={handleCreateClientInline}
                                    disabled={isCreatingClient}
                                    className="w-full py-2.5 bg-clip-500 text-white font-bold rounded-xl hover:bg-clip-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isCreatingClient && <Loader2 size={16} className="animate-spin" />}
                                    {t('new_appointment.new_client.save', 'Aggiungi e continua')}
                                </button>
                            </div>
                        ) : (
                            /* Ricerca cliente */
                            <div className="flex items-start gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        autoFocus
                                        placeholder={t('new_appointment.search_customer', 'Cerca per nome o numero…')}
                                        value={customerInput}
                                        onChange={(e) => {
                                            setCustomerInput(e.target.value);
                                            if (!e.target.value) setSelectedClientId(null);
                                        }}
                                        className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-300 outline-none font-medium text-gray-900 placeholder-gray-400"
                                    />
                                    {showSuggestions && (
                                        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden max-h-56 overflow-y-auto">
                                            {clientSuggestions.length > 0 ? (
                                                clientSuggestions.map((c: any) => (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedClientId(c.id);
                                                            setCustomerInput(c.full_name);
                                                            setShowSuggestions(false);
                                                        }}
                                                        className="w-full text-left px-4 py-3 hover:bg-clip-50 flex justify-between items-center border-b border-gray-50 last:border-0 transition-colors"
                                                    >
                                                        <div>
                                                            <p className="font-bold text-gray-900 text-sm">{c.full_name}</p>
                                                            <p className="text-xs text-gray-500">{c.phone || c.email}</p>
                                                        </div>

                                                        {(() => {
                                                            const isBlocked = blackList?.includes(c.id);
                                                            const reportsCount = allCustomerReports?.filter((r: any) => r.client_id === c.id).length || 0;

                                                            if (isBlocked) {
                                                                return (
                                                                    <div className="flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded-md">
                                                                        <Lock size={12} /> {t('bookings.modal.blocked_title', 'Bloccato')}
                                                                    </div>
                                                                );
                                                            }
                                                            if (reportsCount > 0) {
                                                                return (
                                                                    <div className="flex items-center gap-1 text-[10px] font-bold text-orange-700 bg-orange-50 border border-orange-200 px-2 py-1 rounded-md">
                                                                        <AlertTriangle size={12} /> {reportsCount} {t('bookings.modal.reports_count', 'Segnalazioni')}
                                                                    </div>
                                                                );
                                                            }
                                                            return null;
                                                        })()}
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="p-4 text-center text-sm text-gray-400">
                                                    {t('new_appointment.no_client_found', 'Nessun cliente trovato · usa')}{' '}
                                                    <b className="text-clip-600">+</b>{' '}
                                                    {t('new_appointment.no_client_found_2', 'per aggiungerlo')}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    onClick={openInlineNewClient}
                                    className="h-[44px] w-[44px] flex-shrink-0 flex items-center justify-center bg-clip-50 text-clip-600 rounded-xl hover:bg-clip-100 transition-colors border border-clip-200"
                                    title={t('new_appointment.add_new_client', 'Aggiungi nuovo cliente')}
                                >
                                    <Plus size={20} strokeWidth={2.5} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* --------- SERVIZIO --------- */}
                    <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                            {t('new_appointment.service_label')} *
                        </label>
                        {performancePills.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 mt-1.5 max-h-32 overflow-y-auto custom-scrollbar">
                                {performancePills.map((p: any) => {
                                    const isSelected = p.id === performanceId;
                                    return (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => {
                                                const oldPerf = performances?.find((x: any) => x.id === performanceId);
                                                setPerformanceId(p.id);
                                                // Fuori dalla modalità slot la lista non è filtrata: se cambia
                                                // la durata, data e ora vanno riscelte.
                                                if (!initialPerformanceId && oldPerf?.duration !== p.duration) {
                                                    setSelectedDate(undefined);
                                                    setTime('');
                                                    setShowDetails(true);
                                                }
                                            }}
                                            className={`px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${isSelected
                                                ? 'bg-clip-50 border-clip-500 text-clip-700'
                                                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                                        >
                                            {p.name}
                                            <span className="opacity-60 font-medium"> · {p.duration} min{p.price ? ` · €${p.price}` : ''}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="mt-2 text-sm text-gray-400">
                                {!staffId
                                    ? t('new_appointment.pick_staff_first', "Scegli prima l'operatore.")
                                    : t('new_appointment.no_service_available', 'Nessun servizio disponibile per questo slot.')}
                            </p>
                        )}
                    </div>

                    {/* --------- DETTAGLI (operatore, data, ora) --------- */}
                    {showDetails && (
                        <>
                            <div>
                                {/* 2. STAFF */}
                                <div className="space-y-2 relative z-[60]">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('new_appointment.staff_label')} *</label>
                                    <CustomSelect
                                        value={staffId}
                                        onChange={(newStaffId) => {
                                            setStaffId(newStaffId);

                                            if (initialPerformanceId && selectedDate && time) {
                                                const currentPerf = performances?.find((p: any) => p.id === performanceId);
                                                const currentDuration = currentPerf?.duration || 0;
                                                const currentName = currentPerf?.name || null;
                                                const newStaffPerfs = performances?.filter((p: any) => p.hairdresser_id === newStaffId && p.is_active !== false) || [];

                                                // 1. Assegna servizio con lo stesso nome
                                                if (currentName) {
                                                    const sameServicePerf = newStaffPerfs.find((p: any) => p.name === currentName);
                                                    if (sameServicePerf) {
                                                        const res = validateBookingSlot(
                                                            { bookings, performances, busySlots, organization, users, shiftChanges },
                                                            { date: selectedDate, time, performanceId: sameServicePerf.id, staffId: newStaffId }
                                                        );
                                                        if (res.isValid) {
                                                            setPerformanceId(sameServicePerf.id);
                                                            return;
                                                        }
                                                    }
                                                }

                                                // 2. Assegna servizio della stessa durata
                                                let matchingPerf = newStaffPerfs.find((p: any) => p.duration === currentDuration);
                                                if (matchingPerf) {
                                                    const res = validateBookingSlot(
                                                        { bookings, performances, busySlots, organization, users, shiftChanges },
                                                        { date: selectedDate, time, performanceId: matchingPerf.id, staffId: newStaffId }
                                                    );
                                                    if (res.isValid) {
                                                        setPerformanceId(matchingPerf.id);
                                                        return;
                                                    }
                                                }

                                                // 3. Assegna un qualsiasi altro servizio
                                                for (const p of newStaffPerfs) {
                                                    const res = validateBookingSlot(
                                                        { bookings, performances, busySlots, organization, users, shiftChanges },
                                                        { date: selectedDate, time, performanceId: p.id, staffId: newStaffId }
                                                    );
                                                    if (res.isValid) {
                                                        setPerformanceId(p.id);
                                                        return;
                                                    }
                                                }
                                            }

                                            // Fallback
                                            setPerformanceId('');
                                            setSelectedDate(undefined);
                                            setTime('');
                                        }}
                                        options={activeStaff}
                                        placeholder={t('new_appointment.staff_placeholder')}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* 4. DATA */}
                                <div className="flex flex-col gap-2 relative z-[40]">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('new_appointment.date_label')} *</label>
                                    <DatePicker selected={selectedDate} onSelect={(date) => { setSelectedDate(date); setTime(''); }} placeholder="Seleziona data" className="w-full" disabled={!performanceId} hairdresserId={staffId} />
                                </div>
                                {/* 5. ORA */}
                                <div className="flex flex-col gap-2 relative z-[30]">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                        <Clock size={14} /> {t('new_appointment.time_label')} *
                                    </label>
                                    <TimePicker
                                        value={time}
                                        onChange={setTime}
                                        step={organization?.step || 30}
                                        disabled={!selectedDate || timeConstraints.isClosed}
                                        minTime={timeConstraints.minTime}
                                        maxTime={timeConstraints.maxTime}
                                        breakStart={timeConstraints.breakStart}
                                        breakEnd={timeConstraints.breakEnd}
                                        selectedDate={selectedDate}
                                        hairdresserId={staffId}
                                        serviceDuration={currentServiceDuration}
                                        closingTime={timeConstraints.closingTime}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* 6. RICORRENZA ED EXTRA */}
                    {!showRecurrence ? (
                                <div className="pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowRecurrence(true)}
                                        className="text-sm font-bold text-clip-600 hover:text-clip-700 transition-colors flex items-center gap-1"
                                    >
                                        + Aggiungi ricorrenza
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2 relative z-[20]">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Ricorrenza</label>
                                        <CustomSelect
                                            value={recurrenceType}
                                            onChange={(val) => {
                                                setRecurrenceType(val);
                                                if (val === 'none') setRecurrenceCount('none');
                                                else if (recurrenceCount === 'none') setRecurrenceCount('3');
                                            }}
                                            options={[
                                                { value: 'none', label: 'Nessuna (Evento singolo)' },
                                                { value: 'weekly', label: 'Ogni settimana' },
                                                { value: 'biweekly', label: 'Ogni due settimane' },
                                                { value: 'triweekly', label: 'Ogni tre settimane' },
                                                { value: 'monthly', label: 'Ogni mese' },
                                                { value: 'bimonthly', label: 'Ogni due mesi' }
                                            ]}
                                            placeholder="Seleziona ricorrenza"
                                        />
                                    </div>
                                    <div className="space-y-2 relative z-[15]">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Ripetizioni</label>
                                        <CustomSelect
                                            value={recurrenceCount}
                                            onChange={setRecurrenceCount}
                                            options={
                                                recurrenceType === 'none'
                                                    ? [{ value: 'none', label: 'Nessuna' }]
                                                    : [
                                                        { value: '3', label: '3 Appuntamenti' },
                                                        { value: '5', label: '5 Appuntamenti' },
                                                        { value: '10', label: '10 Appuntamenti' }
                                                    ]
                                            }
                                            placeholder="Seleziona ripetizioni"
                                            disabled={recurrenceType === 'none'}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* 6b. SCONTO O MAGGIORAZIONE */}
                            {!showAdjustment ? (
                                <div className="pt-1">
                                    <button
                                        type="button"
                                        onClick={() => setShowAdjustment(true)}
                                        className="text-sm font-bold text-clip-600 hover:text-clip-700 transition-colors flex items-center gap-1"
                                    >
                                        {t('new_appointment.discount_or_markup_btn', '+ Sconto o Maggiorazione')}
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2 relative z-[13]">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('new_appointment.discount_or_markup_label', 'Sconto o Maggiorazione')}</label>
                                        {/* Sconto o maggiorazione a un tocco, come in agenda */}
                                        <div className="flex bg-gray-100 rounded-xl p-1">
                                            {([
                                                { value: 'discount', label: t('new_appointment.adjustment_type_discount', 'Sconto'), active: 'text-emerald-600' },
                                                { value: 'markup', label: t('new_appointment.adjustment_type_markup', 'Maggiorazione'), active: 'text-blue-600' },
                                            ] as const).map((opt) => {
                                                const isSelected = adjustmentType === opt.value;
                                                return (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => {
                                                            // Ritoccare la stessa voce la disattiva
                                                            const next = isSelected ? 'none' : opt.value;
                                                            setAdjustmentType(next);
                                                            if (next === 'none') setAdjustmentValue('');
                                                        }}
                                                        className={`flex-1 py-2 rounded-lg text-xs font-bold transition-colors ${isSelected ? `bg-white shadow-sm ${opt.active}` : 'text-gray-500 hover:text-gray-700'}`}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="space-y-2 relative z-[12]">
                                        <div className="flex items-center justify-between">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                                {adjustmentUnit === 'percent'
                                                    ? t('new_appointment.value_label_percent', 'Valore (%)')
                                                    : t('new_appointment.value_label', 'Valore (€)')}
                                            </label>
                                            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                                                {(['eur', 'percent'] as const).map((u) => (
                                                    <button
                                                        key={u}
                                                        type="button"
                                                        onClick={() => setAdjustmentUnit(u)}
                                                        disabled={adjustmentType === 'none'}
                                                        className={`px-2.5 py-1 text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${adjustmentUnit === u ? 'bg-clip-500 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                                                    >
                                                        {u === 'eur' ? '€' : '%'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            placeholder="0"
                                            value={adjustmentValue}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === '') setAdjustmentValue('');
                                                else {
                                                    const parsed = parseInt(val, 10);
                                                    setAdjustmentValue(isNaN(parsed) ? '' : parsed.toString());
                                                }
                                            }}
                                            disabled={adjustmentType === 'none'}
                                            className={`w-full pl-4 pr-4 py-3 bg-white border rounded-xl focus:ring-2 focus:ring-clip-200 outline-none font-medium placeholder:text-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed ${(isDiscountTooHigh || isPercentTooHigh) ? 'border-red-500 text-red-900 focus:ring-red-200' : 'border-gray-200 text-gray-900'}`}
                                        />
                                        {adjustmentUnit === 'percent' && adjustmentType !== 'none' && adjustmentEuro > 0 && !isPercentTooHigh && (
                                            <p className="text-xs font-semibold text-gray-500 mt-1">
                                                {adjustmentType === 'discount'
                                                    ? t('new_appointment.discount_percent_preview', '≈ -€{{amount}} sul prezzo del servizio', { amount: adjustmentEuro })
                                                    : t('new_appointment.markup_percent_preview', '≈ +€{{amount}} sul prezzo del servizio', { amount: adjustmentEuro })}
                                            </p>
                                        )}
                                        {isPercentTooHigh && (
                                            <p className="text-xs font-semibold text-red-600 mt-1">{t('new_appointment.percent_too_high_error', 'La percentuale non può superare il 100%')}</p>
                                        )}
                                        {isDiscountTooHigh && !isPercentTooHigh && (
                                            <p className="text-xs font-semibold text-red-600 mt-1">
                                                {t('new_appointment.discount_too_high_error', 'Lo sconto supera il prezzo del servizio (€{{price}})', { price: Math.round(currentServicePrice) })}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* 7. NOTE */}
                            <div className="space-y-2 relative z-[10]">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                    <FileText size={14} /> {t('new_appointment.notes_label')}
                                </label>
                                <textarea rows={2} placeholder={t('new_appointment.notes_placeholder') ?? ''} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 outline-none font-medium text-gray-900 resize-none" />
                            </div>

                    {/* FOOTER */}
                    <div className="pt-4 border-t border-gray-100 flex gap-3">
                        <button onClick={onClose} className="flex-1 py-2.5 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-colors" type="button">
                            {t('common.cancel', 'Annulla')}
                        </button>
                        <PrimaryButton
                            label={isSubmitting ? "Salvataggio..." : t('new_appointment.confirm_btn')}
                            className={`flex-1 justify-center ${!canSubmit ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={handleSubmit}
                            disabled={!canSubmit || isSubmitting}
                            icon={isSubmitting ? Loader2 : undefined}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default NewAppointmentModal;